import { useState, useEffect, useContext, useMemo, useCallback } from "react";

import { message } from "antd";
import { useSelector } from "react-redux";

import { UserContext } from "App/contexts";
import { useAgentPhoneNumberDetails } from "Hooks/useAgentPhoneNumberDetails";
import { useTwilioDevice } from "Hooks/useTwilioDevice/useTwilioDevice";
import { useRemoveTask } from "Pages/agentDesktop/Pages/AgentDesktopTasks/Components/AgentTaskInterface/ActivityView/Header/TaskSummary/TaskSummaryHooks";
import {
    CK_VOICE_CHANNEL,
    LEGACY_VOICE_CHANNEL,
    PRIORITY_CHANNEL,
} from "Pages/agentDesktop/Pages/AgentDesktopTasks/Components/AgentTaskInterface/constants";
import { voicemailDrop } from "Services/ConversationsApiService";
import { renderErrorMessage } from "Services/LoggingService";
import { selectAgentInformation } from "Services/state/agent/AgentInformationSlice";
import {
    selectConferenceSnapshotActiveParticipants,
    selectConferenceSnapshotAgentCallSid,
} from "Services/state/conferences/ConferenceSnapshotSelectors";
import { selectTaskSummaryIsSubmitted } from "Services/state/tasks/Selectors";
import { getAttributes, isPendingInboundConferenceTask, summarizeTask } from "Services/Task.service";

import { BUTTONS_MESSAGES } from "../../CallBar.consts";

import type { ConferenceParticipant, RVTask } from "@regal-voice/shared-types";
import type { AttributesCampaign } from "Types/Campaign";

const MESSAGES = BUTTONS_MESSAGES.voicemailDrop;

export type UseCallActionVMDropOptions = {
    activeConferenceTask?: RVTask;
    disabled?: boolean;
};

export type UseCallActionVMDropResult = {
    dropVoicemail: () => void;
    shouldRenderVoicemailDrop: boolean;
    voicemailDropDisabled: boolean;
};

const useCallActionVMDropData = () => {
    const snapshotCallSid = useSelector(selectConferenceSnapshotAgentCallSid);
    const snapshotActiveParticipants = useSelector(selectConferenceSnapshotActiveParticipants);

    return { participants: snapshotActiveParticipants, agentCallSid: snapshotCallSid };
};

export const useCallActionVMDrop = ({ activeConferenceTask, disabled = false }: UseCallActionVMDropOptions) => {
    const [isDroppingVoicemail, setIsDroppingVoicemail] = useState(false);
    const [isDisabled, setIsDisabled] = useState(disabled);
    const [campaign, setCampaign] = useState<AttributesCampaign>();
    const { brand } = useContext(UserContext);
    const { device } = useTwilioDevice();
    const removeCompletedTask = useRemoveTask();
    const agentInformation = useSelector(selectAgentInformation);

    const { participants, agentCallSid } = useCallActionVMDropData();

    const { regalVoicePhone } = getAttributes(activeConferenceTask);
    const regalVoicePhoneSource = useAgentPhoneNumberDetails(regalVoicePhone);
    const regalVoicePhoneFriendlyName = regalVoicePhoneSource?.internalDisplayName || "";
    const taskSummarySubmitted = useSelector(selectTaskSummaryIsSubmitted)(activeConferenceTask?.sid || "");
    const taskChannel = activeConferenceTask?.channelType;
    const voicemailInstructions = getAttributes(activeConferenceTask as RVTask)?.campaignInfo?.voicemailInstructions;

    const shouldRender = useMemo(
        () => taskChannel && [LEGACY_VOICE_CHANNEL, CK_VOICE_CHANNEL, PRIORITY_CHANNEL].includes(taskChannel),
        [taskChannel]
    );

    const attemptVoiceMailDrop = useCallback(
        async (customerCallSid: string, recordingId?: string) => {
            setIsDroppingVoicemail(true);
            try {
                const { success } = await voicemailDrop({
                    customerCallSid,
                    campaignId: campaign?.id,
                    recordingId,
                });
                // success will be false if the call is not in
                // a state where a voicemail can be dropped
                if (success) {
                    message.success(MESSAGES.success());
                } else {
                    renderErrorMessage({
                        content: MESSAGES.waiting(),
                        logAsWarn: true,
                    });
                }

                return success;
            } catch (err) {
                const error = err instanceof Error ? err : new Error(String(err));
                renderErrorMessage({ content: MESSAGES.error(error.message), error });
            } finally {
                setIsDroppingVoicemail(false);
            }
            return false;
        },
        [campaign]
    );

    const attemptTaskSummary = useCallback(async () => {
        const sid = activeConferenceTask?.sid;
        try {
            if (!brand || !activeConferenceTask) {
                throw new Error("Brand or task not found.");
            }
            const success = await summarizeTask(
                {
                    brand,
                    task: activeConferenceTask,
                    disposition: "Pre-recorded Voicemail",
                    dispositionedBy: "system",
                    regalVoicePhoneFriendlyName,
                    agent: agentInformation,
                },
                taskSummarySubmitted
            );
            if (success) {
                if (sid) {
                    removeCompletedTask(sid);
                }
                device?.disconnectAll();
                message.success("Task successfully summarized.");
            }
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            renderErrorMessage({ content: MESSAGES.error(error.message), error });
        }
    }, [
        brand,
        activeConferenceTask,
        regalVoicePhoneFriendlyName,
        agentInformation,
        taskSummarySubmitted,
        removeCompletedTask,
        device,
    ]);

    const dropVoicemail = useCallback(
        async (recordingId?: string) => {
            // keeping this same logic temporarily to not introduce behavior changes, but it's very misleading since participants is an array
            const customerCallSid = (
                Object.values(participants).find((p) => {
                    return "callSid" in p && p.callSid != agentCallSid;
                }) as Exclude<ConferenceParticipant, { type: "queue" }>
            )?.callSid;
            if (!customerCallSid) {
                return;
            }

            const dropped = await attemptVoiceMailDrop(customerCallSid, recordingId);
            if (dropped) {
                await attemptTaskSummary();
            }
        },
        [participants, agentCallSid, attemptVoiceMailDrop, attemptTaskSummary]
    );

    useEffect(() => {
        if (activeConferenceTask) {
            const campaignInfo = getAttributes(activeConferenceTask).campaignInfo as AttributesCampaign;
            setCampaign(campaignInfo as AttributesCampaign);
            const noVoicemail =
                campaignInfo &&
                !!campaignInfo?.voicemailInstructions &&
                ["empty", "personalized"].includes(campaignInfo?.voicemailInstructions);
            const notInProgress = ["completed", "canceled"].includes(activeConferenceTask.status);
            const inBound = activeConferenceTask?.attributes.direction == "inbound";
            setIsDisabled(
                noVoicemail ||
                    notInProgress ||
                    isPendingInboundConferenceTask(activeConferenceTask) ||
                    disabled ||
                    inBound
            );
        } else {
            setIsDisabled(disabled);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps -- This effect should only run when the task changes
    }, [activeConferenceTask?.status, voicemailInstructions, disabled]);

    return {
        dropVoicemail,
        campaignId: campaign?.id,
        isDroppingVoicemail,
        shouldRenderVoicemailDrop: shouldRender,
        voicemailDropDisabled: isDisabled,
    };
};
